import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import providerService from "../../services/providerService.js";
import logger from "../../utils/logger";

// Fetch provider statistics for dashboard
export const fetchProviderStats = createAsyncThunk(
  "provider/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.getStats();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch statistics";
      logger.error("Error fetching provider stats:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch provider's jobs (accepted bookings)
export const fetchProviderJobs = createAsyncThunk(
  "provider/fetchJobs",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await providerService.getJobs(filters);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch jobs";
      logger.error("Error fetching provider jobs:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch specific job details
export const fetchJobById = createAsyncThunk(
  "provider/fetchJobById",
  async (jobId, { rejectWithValue }) => {
    try {
      const data = await providerService.getJobById(jobId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch job details";
      logger.error("Error fetching job details:", error);
      return rejectWithValue(message);
    }
  },
);

// Complete a job
export const completeJob = createAsyncThunk(
  "provider/completeJob",
  async ({ jobId, finalPrice }, { rejectWithValue }) => {
    try {
      const data = await providerService.completeJob(jobId, { finalPrice });
      return data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to complete job";
      logger.error("Error completing job:", error);
      return rejectWithValue(message);
    }
  },
);

// Cancel a job (provider cancellation)
export const cancelJob = createAsyncThunk(
  "provider/cancelJob",
  async ({ jobId, reason, details }, { rejectWithValue }) => {
    try {
      const data = await providerService.cancelJob(jobId, { reason, details });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel booking";
      logger.error("Error cancelling job:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch provider's booking links (pending requests)
export const fetchBookingLinks = createAsyncThunk(
  "provider/fetchBookingLinks",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.getBookingLinks();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch booking requests";
      logger.error("Error fetching booking links:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch booking link details by ID (private, logged in)
export const fetchBookingLinkById = createAsyncThunk(
  "provider/fetchBookingLinkById",
  async (bookingLinkId, { rejectWithValue }) => {
    try {
      const data = await providerService.getBookingLinkById(bookingLinkId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch request details";
      logger.error("Error fetching booking link details:", error);
      return rejectWithValue(message);
    }
  },
);

// Accept booking link (private, logged in)
export const acceptBookingLink = createAsyncThunk(
  "provider/acceptBookingLink",
  async (bookingLinkId, { rejectWithValue }) => {
    try {
      const data = await providerService.acceptBookingLink(bookingLinkId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to accept request";
      logger.error("Error accepting booking link:", error);
      return rejectWithValue(message);
    }
  },
);

// Decline booking link (private, logged in)
export const declineBookingLink = createAsyncThunk(
  "provider/declineBookingLink",
  async (bookingLinkId, { rejectWithValue }) => {
    try {
      const data = await providerService.declineBookingLink(bookingLinkId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to decline request";
      logger.error("Error declining booking link:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch public request details (token-based, no auth)
export const fetchPublicRequest = createAsyncThunk(
  "provider/fetchPublicRequest",
  async (token, { rejectWithValue }) => {
    try {
      const data = await providerService.getPublicRequest(token);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch request details";
      logger.error("Error fetching public request:", error);
      return rejectWithValue(message);
    }
  },
);

// Accept booking request (token-based, no auth)
export const acceptBookingRequest = createAsyncThunk(
  "provider/acceptRequest",
  async (token, { rejectWithValue }) => {
    try {
      const data = await providerService.acceptRequest(token);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to accept request";
      logger.error("Error accepting request:", error);
      return rejectWithValue(message);
    }
  },
);

// Decline booking request (token-based, no auth)
export const declineBookingRequest = createAsyncThunk(
  "provider/declineRequest",
  async (token, { rejectWithValue }) => {
    try {
      const data = await providerService.declineRequest(token);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to decline request";
      logger.error("Error declining request:", error);
      return rejectWithValue(message);
    }
  },
);

// Fetch provider's categories
export const fetchProviderCategories = createAsyncThunk(
  "provider/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.getCategories();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch categories";
      logger.error("Error fetching provider categories:", error);
      return rejectWithValue(message);
    }
  },
);

// Update provider categories
export const updateProviderCategories = createAsyncThunk(
  "provider/updateCategories",
  async (data, { rejectWithValue }) => {
    try {
      const response = await providerService.updateCategories(data);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update categories";
      logger.error("Error updating provider categories:", error);
      return rejectWithValue(message);
    }
  },
);

// Stripe Connect - Create account link for onboarding
export const createStripeAccountLink = createAsyncThunk(
  "provider/createStripeAccountLink",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.createStripeAccountLink();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create Stripe account link";
      logger.error("Error creating Stripe account link:", error);
      return rejectWithValue(message);
    }
  },
);

// Stripe Connect - Update account link
export const updateStripeAccountLink = createAsyncThunk(
  "provider/updateStripeAccountLink",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.updateStripeAccountLink();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update Stripe account link";
      logger.error("Error updating Stripe account link:", error);
      return rejectWithValue(message);
    }
  },
);

// Stripe Connect - Get account status
export const fetchStripeAccountStatus = createAsyncThunk(
  "provider/fetchStripeAccountStatus",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.getStripeAccountStatus();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to fetch Stripe account status";
      logger.error("Error fetching Stripe account status:", error);
      return rejectWithValue(message);
    }
  },
);

// Stripe Connect - Get dashboard link
export const getStripeDashboardLink = createAsyncThunk(
  "provider/getStripeDashboardLink",
  async (_, { rejectWithValue }) => {
    try {
      const data = await providerService.getStripeDashboardLink();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get Stripe dashboard link";
      logger.error("Error getting Stripe dashboard link:", error);
      return rejectWithValue(message);
    }
  },
);

const initialState = {
  // Stats
  stats: null,
  statsLoading: false,
  statsError: null,

  // Jobs
  jobs: [],
  jobsLoading: false,
  jobsError: null,

  // Current job details
  currentJob: null,
  currentJobLoading: false,
  currentJobError: null,

  // Booking links (private, logged in)
  bookingLinks: [],
  bookingLinksLoading: false,
  bookingLinksError: null,

  // Current booking link details (private)
  currentBookingLink: null,
  currentBookingLinkLoading: false,
  currentBookingLinkError: null,

  // Public request (token-based)
  publicRequest: null,
  publicRequestLoading: false,
  publicRequestError: null,

  // Categories
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  // Stripe Connect
  stripeAccountStatus: null,
  stripeAccountLoading: false,
  stripeAccountError: null,
};

const providerSlice = createSlice({
  name: "provider",
  initialState,
  reducers: {
    clearCurrentJob: (state) => {
      state.currentJob = null;
      state.currentJobError = null;
    },
    clearCurrentBookingLink: (state) => {
      state.currentBookingLink = null;
      state.currentBookingLinkError = null;
    },
    clearPublicRequest: (state) => {
      state.publicRequest = null;
      state.publicRequestError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch stats
    builder
      .addCase(fetchProviderStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchProviderStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchProviderStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      });
    // Fetch jobs
    builder
      .addCase(fetchProviderJobs.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = null;
      })
      .addCase(fetchProviderJobs.fulfilled, (state, action) => {
        state.jobsLoading = false;
        state.jobs = action.payload.bookings || action.payload.results || [];
      })
      .addCase(fetchProviderJobs.rejected, (state, action) => {
        state.jobsLoading = false;
        state.jobsError = action.payload;
      });

    // Fetch job by ID
    builder
      .addCase(fetchJobById.pending, (state) => {
        state.currentJobLoading = true;
        state.currentJobError = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.currentJobLoading = false;
        state.currentJob = action.payload;
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.currentJobLoading = false;
        state.currentJobError = action.payload;
      });

    // Complete job
    builder
      .addCase(completeJob.pending, (state) => {
        state.currentJobLoading = true;
      })
      .addCase(completeJob.fulfilled, (state, action) => {
        state.currentJobLoading = false;
        state.currentJob = action.payload;
        // Update in jobs list
        const index = state.jobs.findIndex((j) => j.id === action.payload.id);
        if (index !== -1) {
          state.jobs[index] = action.payload;
        }
      })
      .addCase(completeJob.rejected, (state, action) => {
        state.currentJobLoading = false;
        state.currentJobError = action.payload;
      });

    // Cancel job (provider cancellation)
    builder
      .addCase(cancelJob.pending, (state) => {
        state.currentJobLoading = true;
      })
      .addCase(cancelJob.fulfilled, (state) => {
        state.currentJobLoading = false;
        // API returns { success, message } — patch status locally
        if (state.currentJob) {
          state.currentJob = {
            ...state.currentJob,
            status: "provider_cancelled",
          };
          const index = state.jobs.findIndex(
            (j) => j.id === state.currentJob.id,
          );
          if (index !== -1) {
            state.jobs[index] = state.currentJob;
          }
        }
      })
      .addCase(cancelJob.rejected, (state, action) => {
        state.currentJobLoading = false;
        state.currentJobError = action.payload;
      });

    // Fetch booking links
    builder
      .addCase(fetchBookingLinks.pending, (state) => {
        state.bookingLinksLoading = true;
        state.bookingLinksError = null;
      })
      .addCase(fetchBookingLinks.fulfilled, (state, action) => {
        state.bookingLinksLoading = false;
        state.bookingLinks = action.payload.results || action.payload;
      })
      .addCase(fetchBookingLinks.rejected, (state, action) => {
        state.bookingLinksLoading = false;
        state.bookingLinksError = action.payload;
      });

    // Fetch booking link by ID
    builder
      .addCase(fetchBookingLinkById.pending, (state) => {
        state.currentBookingLinkLoading = true;
        state.currentBookingLinkError = null;
      })
      .addCase(fetchBookingLinkById.fulfilled, (state, action) => {
        state.currentBookingLinkLoading = false;
        state.currentBookingLink = action.payload;
      })
      .addCase(fetchBookingLinkById.rejected, (state, action) => {
        state.currentBookingLinkLoading = false;
        state.currentBookingLinkError = action.payload;
      });

    // Accept booking link
    builder
      .addCase(acceptBookingLink.pending, (state) => {
        state.currentBookingLinkLoading = true;
      })
      .addCase(acceptBookingLink.fulfilled, (state, action) => {
        state.currentBookingLinkLoading = false;
        // Remove from bookingLinks array since it's now accepted
        state.bookingLinks = state.bookingLinks.filter(
          (link) => link.id !== action.meta.arg,
        );
        // Optimistically decrement pending requests count in stats
        if (state.stats?.pendingRequests) {
          state.stats.pendingRequests = state.stats.pendingRequests.filter(
            (r) => r.id !== action.meta.arg,
          );
        }
      })
      .addCase(acceptBookingLink.rejected, (state, action) => {
        state.currentBookingLinkLoading = false;
        state.currentBookingLinkError = action.payload;
      });

    // Decline booking link
    builder
      .addCase(declineBookingLink.pending, (state) => {
        state.currentBookingLinkLoading = true;
      })
      .addCase(declineBookingLink.fulfilled, (state, action) => {
        state.currentBookingLinkLoading = false;
        // Remove from bookingLinks array since it's now declined
        state.bookingLinks = state.bookingLinks.filter(
          (link) => link.id !== action.meta.arg,
        );
        // Optimistically decrement pending requests count in stats
        if (state.stats?.pendingRequests) {
          state.stats.pendingRequests = state.stats.pendingRequests.filter(
            (r) => r.id !== action.meta.arg,
          );
        }
      })
      .addCase(declineBookingLink.rejected, (state, action) => {
        state.currentBookingLinkLoading = false;
        state.currentBookingLinkError = action.payload;
      });

    // Fetch public request
    builder
      .addCase(fetchPublicRequest.pending, (state) => {
        state.publicRequestLoading = true;
        state.publicRequestError = null;
      })
      .addCase(fetchPublicRequest.fulfilled, (state, action) => {
        state.publicRequestLoading = false;
        state.publicRequest = action.payload;
      })
      .addCase(fetchPublicRequest.rejected, (state, action) => {
        state.publicRequestLoading = false;
        state.publicRequestError = action.payload;
      });

    // Accept request
    builder
      .addCase(acceptBookingRequest.pending, (state) => {
        state.publicRequestLoading = true;
      })
      .addCase(acceptBookingRequest.fulfilled, (state) => {
        state.publicRequestLoading = false;
      })
      .addCase(acceptBookingRequest.rejected, (state, action) => {
        state.publicRequestLoading = false;
        state.publicRequestError = action.payload;
      });

    // Decline request
    builder
      .addCase(declineBookingRequest.pending, (state) => {
        state.publicRequestLoading = true;
      })
      .addCase(declineBookingRequest.fulfilled, (state) => {
        state.publicRequestLoading = false;
      })
      .addCase(declineBookingRequest.rejected, (state, action) => {
        state.publicRequestLoading = false;
        state.publicRequestError = action.payload;
      });

    // Fetch provider categories
    builder
      .addCase(fetchProviderCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchProviderCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchProviderCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      });

    // Update provider categories
    builder
      .addCase(updateProviderCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(updateProviderCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(updateProviderCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      });

    // Stripe Connect - Fetch account status
    builder
      .addCase(fetchStripeAccountStatus.pending, (state) => {
        state.stripeAccountLoading = true;
        state.stripeAccountError = null;
      })
      .addCase(fetchStripeAccountStatus.fulfilled, (state, action) => {
        state.stripeAccountLoading = false;
        state.stripeAccountStatus = action.payload;
      })
      .addCase(fetchStripeAccountStatus.rejected, (state, action) => {
        state.stripeAccountLoading = false;
        state.stripeAccountError = action.payload;
      });
  },
});

export const { clearCurrentJob, clearCurrentBookingLink, clearPublicRequest } =
  providerSlice.actions;

// Selectors
export const selectStats = (state) => state.provider.stats;
export const selectStatsLoading = (state) => state.provider.statsLoading;
export const selectStatsError = (state) => state.provider.statsError;

export const selectJobs = (state) => state.provider.jobs;
export const selectJobsLoading = (state) => state.provider.jobsLoading;
export const selectJobsError = (state) => state.provider.jobsError;

export const selectCurrentJob = (state) => state.provider.currentJob;
export const selectCurrentJobLoading = (state) =>
  state.provider.currentJobLoading;
export const selectCurrentJobError = (state) => state.provider.currentJobError;

export const selectPublicRequest = (state) => state.provider.publicRequest;
export const selectPublicRequestLoading = (state) =>
  state.provider.publicRequestLoading;
export const selectPublicRequestError = (state) =>
  state.provider.publicRequestError;

export const selectBookingLinks = (state) => state.provider.bookingLinks;
export const selectBookingLinksLoading = (state) =>
  state.provider.bookingLinksLoading;
export const selectBookingLinksError = (state) =>
  state.provider.bookingLinksError;

export const selectCurrentBookingLink = (state) =>
  state.provider.currentBookingLink;
export const selectCurrentBookingLinkLoading = (state) =>
  state.provider.currentBookingLinkLoading;
export const selectCurrentBookingLinkError = (state) =>
  state.provider.currentBookingLinkError;

export const selectProviderCategories = (state) => state.provider.categories;
export const selectProviderCategoriesLoading = (state) =>
  state.provider.categoriesLoading;
export const selectProviderCategoriesError = (state) =>
  state.provider.categoriesError;

export const selectStripeAccountStatus = (state) =>
  state.provider.stripeAccountStatus;
export const selectStripeAccountLoading = (state) =>
  state.provider.stripeAccountLoading;
export const selectStripeAccountError = (state) =>
  state.provider.stripeAccountError;

export default providerSlice.reducer;
